/**
 * Single source of truth for hour rounding and aggregation, shared by client and server
 * (PRD §6.1). Raw hours are never rounded at storage or during summation — only at
 * display/export. No inline `reduce` over hours anywhere else.
 */

import { WORK_PROFILE_LIMITS_DEFAULT } from "./work-profile-limits";

/** Round half away from zero to 2 decimals. THE only rounding rule (PRD §6.1). */
export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return ((n < 0 ? -1 : 1) * Math.round(Math.abs(n) * 100 + 1e-9)) / 100;
}

/**
 * Round half away from zero to 1 decimal — the granularity the calendar hour inputs accept.
 * Display/input only; stored and summed hours keep their raw precision (PRD §6.1).
 */
export function round1(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return ((n < 0 ? -1 : 1) * Math.round(Math.abs(n) * 10 + 1e-9)) / 10;
}

/** Quarter-hour rounding used by the read-only "F&E (Antrag)" column (PRD §6.2). */
export function roundToQuarter(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return ((n < 0 ? -1 : 1) * Math.round(Math.abs(n) * 4 + 1e-9)) / 4;
}

/**
 * Percentage of `actual / planned` clamped to [0, 100], rounded to an integer.
 * `planned === 0` returns 100 if there is any actual, else 0 (PRD §6.1).
 */
export function completionPct(actual: number, planned: number): number {
  if (planned > 0) {
    return Math.min(100, Math.round((actual / planned) * 100));
  }
  return actual > 0 ? 100 : 0;
}

/** Weekly tracking RAG for dashboard ranking: none when no elapsed weeks yet. */
export type WeeklyRag = "red" | "amber" | "green" | "none";

export function weeklyTrackingRag(weeksSaved: number, weeksTotal: number): WeeklyRag {
  if (weeksTotal <= 0) return "none";
  if (weeksSaved <= 0) return "red";
  if (weeksSaved >= weeksTotal) return "green";
  return "amber";
}

/**
 * R&D share of total hours as an integer percent for Live Übersicht / year-matrix export.
 * Returns `null` when the year (or period) is empty (both totals 0) → UI shows "—".
 * When total > 0 and rd = 0 → 0. When total > 0 and rd > 0 → Math.round(rd/total*100).
 * Do not confuse with `completionPct` (actual/planned).
 */
export function rdSharePct(rdHours: number, totalHours: number): number | null {
  if (totalHours <= 0 && rdHours <= 0) return null;
  if (totalHours <= 0) return rdHours > 0 ? 100 : null;
  return Math.min(100, Math.round((rdHours / totalHours) * 100));
}

/**
 * Hard write ceiling on a person's day (PRD §6.2 / §6.4 D3).
 * This is the admin setting `maxHoursPerWorkday` alone — not the contract day.
 * Fall back to the product default when the value is not a finite positive number.
 */
export function dailyCap(
  maxHoursPerWorkday: number = WORK_PROFILE_LIMITS_DEFAULT.maxHoursPerWorkday,
): number {
  if (!Number.isFinite(maxHoursPerWorkday) || maxHoursPerWorkday <= 0) {
    return WORK_PROFILE_LIMITS_DEFAULT.maxHoursPerWorkday;
  }
  return maxHoursPerWorkday;
}

/**
 * Contract / expectation day (PRD §6.2 reports and prefills):
 * min(weekly / workdays, maxHoursPerWorkday).
 * Used by utilization buckets, overbooked-days (soft overrun), and `defaultDay`.
 * Never use this as the write-time D3 ceiling — that is `dailyCap`.
 */
export function contractDay(
  weeklyHours: number,
  workdaysPerWeek: number,
  maxHoursPerWorkday: number = WORK_PROFILE_LIMITS_DEFAULT.maxHoursPerWorkday,
): number {
  const workdays = Math.min(7, Math.max(1, workdaysPerWeek));
  const maxDay = Number.isFinite(maxHoursPerWorkday)
    ? Math.max(0, maxHoursPerWorkday)
    : WORK_PROFILE_LIMITS_DEFAULT.maxHoursPerWorkday;
  return Math.min(weeklyHours / workdays, maxDay);
}

/** Display prefill for a day, clamped to the contract day (PRD §6.2). */
export function defaultDay(
  weeklyHours: number,
  workdaysPerWeek: number,
  maxHoursPerWorkday: number = WORK_PROFILE_LIMITS_DEFAULT.maxHoursPerWorkday,
): number {
  return contractDay(weeklyHours, workdaysPerWeek, maxHoursPerWorkday);
}

/** The read-only "F&E (Antrag)" value for a day: round(total × rd_share × 4) / 4 (PRD §6.2). */
export function rdApplied(totalHours: number, rdShare: number): number {
  return roundToQuarter(totalHours * rdShare);
}

/** Alias used by the calendar UI — same formula as `rdApplied` (PLAN.md 4.2). */
export const plannedRd = rdApplied;
